// components/RepliesPage/tabs/PendingTab.tsx
import { MessageCircle, RefreshCw, LoaderCircle } from "lucide-react";
import PendingReplyCard from "@/components/RepliesPage/ReplyCards/PendingReplyCard";
import InfluencerEngagementCard from "@/components/RepliesPage/ReplyCards/InfluencerEngagementCard";
import EmptyTabState from "./EmptyTabState";

interface PendingTabProps {
  isLoading: boolean;
  replies: any[];
  personaData: any;
  automatedReplies: boolean;
  onApprove: () => void;
  onReject: () => void;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

const PendingTab: React.FC<PendingTabProps> = ({
  isLoading,
  replies,
  personaData,
  automatedReplies,
  onApprove,
  onReject,
  onRefresh,
  isRefreshing,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="flex flex-col items-center">
          <LoaderCircle className="h-8 w-8 animate-spin mb-2" />
          <p className="text-sm text-muted-foreground mt-2">
            Loading pending replies...
          </p>
        </div>
      </div>
    );
  }

  if (replies.length === 0) {
    return (
      <EmptyTabState
        icon={<MessageCircle className="h-6 w-6" />}
        title="No pending replies found"
        description={
          automatedReplies
            ? "Replies are being automatically posted to Twitter."
            : "New replies will appear here for your approval."
        }
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        actionText="Check Again"
        actionIcon={<RefreshCw className="h-4 w-4 mr-2" />}
      />
    );
  }

  return (
    <div className="space-y-4 overflow-y-auto max-h-[calc(100vh-18rem)] scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent">
      {replies.map((reply) => {
        // Check if this is an influencer engagement
        const isInfluencerEngagement =
          reply.message_type === "influencer_engagement";

        return isInfluencerEngagement ? (
          <InfluencerEngagementCard
            key={reply.doc_id}
            reply={reply}
            personaData={personaData}
            onApprove={onApprove}
            onReject={onReject}
            automatedReplies={automatedReplies}
          />
        ) : (
          <PendingReplyCard
            key={reply.doc_id}
            reply={reply}
            personaData={personaData}
            onApprove={onApprove}
            onReject={onReject}
            automatedReplies={automatedReplies}
          />
        );
      })}
    </div>
  );
};

export default PendingTab;
